import localeStore from '@app/src/common/store/LocaleStore';
import AppStore from '@app/src/common/store/AppStore';

export namespace DateTimeService {
  export const MILLISECONDS = 1000;
  export const SECONDS = 60;
  export const MINUTES = 60;
  const WITH_SECONDS_LENGTH = 8;
  const WITHOUT_SECONDS_LENGTH = 5;

  export function parseDateAccordingTimeZone(date: string | Date) {
    const currentDate = new Date(date);
    if (!AppStore.changedTimezone) {
      return currentDate;
    }
    const offsetDiff =
      currentDate.getTimezoneOffset() - AppStore.currentTimezoneOffset;
    const dateWithOffset = new Date();
    dateWithOffset.setTime(
      currentDate.getTime() + offsetDiff * SECONDS * MILLISECONDS
    );
    return dateWithOffset;
  }

  export function reduceTimeZone(date: string | Date) {
    const currentDate = new Date(date);
    if (!AppStore.changedTimezone) {
      return currentDate;
    }
    const offsetDiff =
      currentDate.getTimezoneOffset() - AppStore.currentTimezoneOffset;
    const dateWithOffset = new Date();
    dateWithOffset.setTime(
      currentDate.getTime() - offsetDiff * SECONDS * MILLISECONDS
    );
    return dateWithOffset;
  }

  export function isLicenseWillExpire(date: string) {
    const warningTime = 1728000000; // 20 days
    return Date.parse(date) - Date.now() < warningTime;
  }

  export function isDateExpire(time: string | number) {
    if (typeof time === 'string') {
      return Date.now() > Date.parse(time);
    }
    return Date.now() > time;
  }

  export function isTimestampNearToExpire(
    timestamp: number,
    shift: number = 10
  ): boolean {
    // exp date is in seconds
    return Math.floor(Date.now() / MILLISECONDS) < timestamp - shift;
  }

  export function getTodaysDay() {
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setHours(23, 59, 59, 999);
    return {
      start: start.getTime(),
      end: end.getTime(),
      startIso: start.toISOString(),
      endIso: end.toISOString()
    };
  }

  export function getToday(time: string, withSeconds?: boolean) {
    const today = new Date();
    return timeStringToDate(today, time, withSeconds);
  }

  export function getTomorrowsDay() {
    return DateTimeService.getShiftDay(1);
  }

  export function getYesterdaysDay() {
    return DateTimeService.getShiftDay(-1);
  }

  export function getShiftDay(shift: number) {
    const start = new Date();
    start.setDate(start.getDate() + shift);
    start.setHours(0, 0, 0, 0);
    const end = new Date();
    end.setDate(end.getDate() + shift);
    end.setHours(23, 59, 59, 999);
    return {
      start: start.getTime(),
      end: end.getTime(),
      startIso: start.toISOString(),
      endIso: end.toISOString()
    };
  }

  export function getMinutes(milliseconds: number): number {
    return Math.floor(milliseconds / MILLISECONDS / SECONDS);
  }

  export function parseMSValueToSMH(ms: number): [number, number, number] {
    return [
      Math.floor((ms / MILLISECONDS) % SECONDS),
      Math.floor((ms / MILLISECONDS / MINUTES) % MINUTES),
      Math.floor(ms / MILLISECONDS / (SECONDS * MINUTES))
    ];
  }

  export function convertHMStoMS(
    hours: number,
    minutes: number,
    seconds: number
  ): number {
    return (
      hours * MINUTES * SECONDS * MILLISECONDS +
      minutes * MINUTES * MILLISECONDS +
      seconds * MILLISECONDS
    );
  }

  export function msToOptionalHHMMSS(ms: number): string {
    if (!ms || ms < MILLISECONDS) {
      return '0';
    }

    const [s, m, h] = parseMSValueToSMH(ms);

    const date = [m.toString().padStart(2, '0'), s.toString().padStart(2, '0')];

    if (h) {
      date.unshift(h.toString().padStart(2, '0'));
    }

    return date.join(':');
  }

  export function msToOptionalHHhMMminSSsec(ms: number): string {
    if (!ms || ms < MILLISECONDS) {
      return '0';
    }

    const [s, m, h] = parseMSValueToSMH(ms);
    const date: string[] = [];

    if (h) {
      date.push(`${h}${localeStore.formatMessage('units.hours.short')}`);
      if (s) {
        date.push(
          `${m}${localeStore.formatMessage('units.minutes.short')}`,
          `${s}${localeStore.formatMessage('units.seconds.short')}`
        );
      } else if (m) {
        date.push(`${m}${localeStore.formatMessage('units.minutes.short')}`);
      }
    } else {
      if (m) {
        date.push(`${m}${localeStore.formatMessage('units.minutes.short')}`);
      }
      if (s) {
        date.push(`${s}${localeStore.formatMessage('units.seconds.short')}`);
      }
    }

    return date.join(' : ');
  }

  export function duration(date: string, dateEnd: string): string {
    const startDate = new Date(date).getTime();
    const endDate = (dateEnd ? new Date(dateEnd) : new Date()).getTime();
    const _duration = Math.round((endDate - startDate) / MILLISECONDS);
    let stringValue;

    const seconds = _duration % 60;
    const resultInMinutes = Math.floor(_duration / 60);

    const minutes = resultInMinutes % 60;
    const resultInHours = Math.floor(resultInMinutes / 60);

    const hours = resultInHours % 24;
    const resultInDays = Math.floor(resultInHours / 24);

    if (_duration < 60) {
      stringValue = `${_duration}s`;
    } else if (!resultInHours) {
      stringValue = `${resultInMinutes}m ${seconds}s`;
    } else if (!resultInDays) {
      stringValue = `${resultInHours}h ${minutes}m ${seconds}s`;
    } else {
      stringValue = `${resultInDays}d ${hours}h ${minutes}m ${seconds}s`;
    }
    return stringValue;
  }

  export function parseDate(dateISO: string, withSeconds?: boolean) {
    const date = new Date(dateISO);
    return new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      withSeconds ? date.getUTCSeconds() : 0
    );
  }

  export function dateToISO(date: string | Date, withSeconds?: boolean) {
    const originDate = new Date(date);
    return new Date(
      Date.UTC(
        originDate.getFullYear(),
        originDate.getMonth(),
        originDate.getDate(),
        originDate.getHours(),
        originDate.getMinutes(),
        withSeconds ? originDate.getSeconds() : 0
      )
    ).toISOString();
  }

  export function formatTimeForServer(
    value: null | string | number | Date,
    withSeconds?: boolean
  ): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    const minuteStr =
      date.getMinutes() > 9
        ? date.getMinutes().toString()
        : '0' + date.getMinutes();
    const hourStr =
      date.getHours() > 9 ? date.getHours().toString() : '0' + date.getHours();
    if (withSeconds) {
      const secondStr =
        date.getSeconds() > 9
          ? date.getSeconds().toString()
          : '0' + date.getSeconds();
      return `${hourStr}:${minuteStr}:${secondStr}`;
    }
    return `${hourStr}:${minuteStr}`;
  }

  export function getDurationString(start: Date, end: Date) {
    let minutes = Math.round((end.getTime() - start.getTime()) / 60000);
    const hours = Math.floor(minutes / 60);
    minutes = minutes - hours * 60;

    const minuteStr = minutes > 9 ? minutes.toString() : '0' + minutes;
    const hourStr = hours > 9 ? hours.toString() : '0' + hours;
    return `${hourStr}:${minuteStr}`;
  }

  export function toDateString(
    date: string | Date,
    locale?: string,
    checkTZ?: boolean
  ) {
    if (!date) return '';
    const formatter = new Intl.DateTimeFormat(locale);
    return formatter.format(
      checkTZ
        ? DateTimeService.parseDateAccordingTimeZone(date)
        : new Date(date)
    );
  }

  export function toWithWeekDayDateString(date: string | Date, locale: string) {
    if (!date) return '';
    const options: any = {
      weekday: 'long',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric'
    };
    const formatter = new Intl.DateTimeFormat(locale, options);
    return formatter.format(new Date(date));
  }

  export function getWeekdayName(
    date: string | Date,
    locale: string = 'ru-RU',
    format: 'long' | 'short' | 'narrow' = 'short'
  ): string {
    if (!date) return '';

    return new Intl.DateTimeFormat(locale, {
      weekday: format
    }).format(new Date(date));
  }

  export function toTimeString(
    date: string | Date | null,
    withSeconds: boolean = false,
    useGMT: boolean = false,
    checkTZ?: boolean
  ) {
    if (!date) return '';
    const options = {
      hour: 'numeric',
      minute: 'numeric',
      ...(withSeconds ? { second: 'numeric' } : {}),
      ...(useGMT ? { timeZone: 'GMT' } : {})
    };

    const formatter = new Intl.DateTimeFormat(undefined, options as any);
    return formatter.format(
      checkTZ
        ? DateTimeService.parseDateAccordingTimeZone(date)
        : new Date(date)
    );
  }

  export function toDateTimeString(
    date: string | Date,
    withSeconds: boolean = false,
    checkTZ?: boolean
  ) {
    if (!date) return '';
    return `${toDateString(date, undefined, checkTZ)} ${toTimeString(date, withSeconds, false, checkTZ)}`;
  }

  export function getDateFormat() {
    const formatter = new Intl.DateTimeFormat();
    const formatParts = formatter.formatToParts(new Date(1992, 0, 7, 0, 0, 0));
    const parts: string[] = [];
    formatParts.forEach((part: Intl.DateTimeFormatPart) => {
      switch (part.type) {
        case 'literal':
          parts.push(part.value);
          break;
        case 'day':
          parts.push(''.padStart(part.value.length, 'd'));
          break;
        case 'month':
          parts.push(''.padStart(part.value.length, 'M'));
          break;
        case 'year':
          parts.push(''.padStart(part.value.length, 'y'));
          break;
      }
    });
    return parts.join('');
  }

  export function getTimeFormat(withSeconds?: boolean) {
    const options = {
      hour: 'numeric',
      minute: 'numeric',
      ...(withSeconds ? { second: 'numeric' } : {})
    };

    const formatter = new Intl.DateTimeFormat(undefined, options as any);
    const formatParts = formatter.formatToParts(new Date(1992, 0, 7, 0, 0, 0));
    const formatOptions = formatter.resolvedOptions();
    const parts: string[] = [];
    formatParts.forEach((part: Intl.DateTimeFormatPart) => {
      switch (part.type) {
        case 'literal':
          parts.push(part.value);
          break;
        case 'second':
          parts.push(''.padStart(part.value.length, 's'));
          break;
        case 'minute':
          parts.push(''.padStart(part.value.length, 'm'));
          break;
        case 'hour':
          parts.push(
            ''.padStart(part.value.length, formatOptions.hour12 ? 'h' : 'H')
          );
          break;
        case 'dayPeriod':
          parts.push(''.padStart(part.value.length, 'a'));
          break;
      }
    });
    return parts.join('');
  }

  export function getDateTimeFormat(withSeconds?: boolean) {
    return (
      DateTimeService.getDateFormat() +
      ' ' +
      DateTimeService.getTimeFormat(withSeconds)
    );
  }

  export function dateStringToDate(
    date: string,
    hasTime: boolean,
    withSeconds?: boolean
  ) {
    if (isNaN(new Date(date).getTime())) {
      const format = DateTimeService.getDateFormat().toLowerCase();
      const dayIndex = format.indexOf('dd');
      const monthIndex = format.indexOf('mm');
      const yearIndex = format.indexOf('yyyy');

      let newDate = new Date();
      newDate.setDate(+date.substring(dayIndex, dayIndex + 2));
      newDate.setMonth(+date.substring(monthIndex, monthIndex + 2));
      newDate.setFullYear(+date.substring(yearIndex, yearIndex + 4));

      if (hasTime) {
        const time = date.substring(date.indexOf(' ') + 1);
        newDate = DateTimeService.timeStringToDate(newDate, time, withSeconds);
      }
      return newDate;
    } else {
      return new Date(date);
    }
  }

  export function timeStringToDate(
    currentDate: Date | string,
    time: string,
    withSeconds?: boolean
  ) {
    const maxLength = withSeconds
      ? WITH_SECONDS_LENGTH
      : WITHOUT_SECONDS_LENGTH;
    if (time.length > maxLength) {
      time = time.substring(0, maxLength);
    }
    const times = time.split(':');
    const date = new Date(currentDate);
    date.setHours(+times[0].substring(0, 2));
    date.setMinutes(+times[1] ? +times[1].substring(0, 2) : 0);
    if (withSeconds) {
      date.setSeconds(+times[2] ? +times[2].substring(0, 2) : 0);
    }
    return date;
  }

  function stdTimezoneOffset(currentDate: Date) {
    const jan = new Date(currentDate.getFullYear(), 0, 1);
    const jul = new Date(currentDate.getFullYear(), 6, 1);
    return Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());
  }

  function isDstObserved(currentDate: Date | string) {
    const date = new Date(currentDate);
    return date.getTimezoneOffset() < stdTimezoneOffset(date);
  }

  export function dividePeriodWithDST(start: Date, end: Date) {
    // use 12h because of time switch is 2a.m.
    const eDate = new Date(
      end.getFullYear(),
      end.getMonth(),
      end.getDate(),
      12
    );
    const sDate = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate(),
      12
    );
    const periods = [];
    let currentStart = new Date(start);
    let isDST = isDstObserved(sDate);
    for (sDate; sDate <= eDate; sDate.setDate(sDate.getDate() + 1)) {
      if (isDST !== isDstObserved(sDate)) {
        periods.push({
          start: currentStart,
          end: new Date(
            sDate.getFullYear(),
            sDate.getMonth(),
            sDate.getDate() - 1,
            end.getHours(),
            end.getMinutes()
          )
        });
        isDST = !isDST;
        currentStart = new Date(
          sDate.getFullYear(),
          sDate.getMonth(),
          sDate.getDate(),
          start.getHours(),
          start.getMinutes()
        );
      }
    }
    periods.push({
      start: currentStart,
      end
    });
    return periods;
  }

  export function startOfTheDay(date: Date) {
    date.setHours(0, 0, 0, 0);
    return date;
  }

  export function endOfTheDay(date: Date) {
    date.setHours(23, 59, 59, 999);
    return date;
  }

  export function getDateFromTime(timeString: string) {
    if (!timeString) {
      return '';
    }
    const date = new Date();
    const time = timeString.split(':');
    date.setHours(
      parseInt(time[0], 10),
      parseInt(time[1], 10),
      parseInt(time[2], 10),
      0
    );
    return date as any;
  }

  export function getFormattedTime(date: Date, divider = '.') {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const hoursString = hours < 10 ? '0' + hours : hours;
    const minutesString = minutes < 10 ? '0' + minutes : minutes;
    return `${hoursString}${divider}${minutesString}`;
  }
}
